import { useEffect } from 'react';

/**
 * Lightweight runtime SEO updater.
 *
 * We deliberately avoid pulling in `react-helmet-async` for what is
 * really just <title> + a handful of <meta> tags. This component
 * mounts once per route, updates the document head imperatively, and
 * restores the previous values on unmount so we don't bleed metadata
 * across navigations.
 *
 * Props (all optional except where noted):
 *   title       — full document title; auto-suffixed with " · Kids WiseApp"
 *                 unless the prop already contains "Kids WiseApp" or
 *                 `appendSuffix` is set to false.
 *   description — meta description (overrides og + twitter desc too).
 *   canonical   — absolute URL for <link rel="canonical">. Without one
 *                 we leave whatever index.html ships with.
 *   image       — absolute URL for og:image / twitter:image (1200x630).
 *   noindex     — when true, sets robots to "noindex, nofollow" so
 *                 logged-in dashboards don't appear in search results.
 *   appendSuffix — disable the " · Kids WiseApp" suffix.
 */
export function SEO({
  title,
  description,
  canonical,
  image,
  noindex = false,
  appendSuffix = true,
}) {
  useEffect(() => {
    const prev = {
      title: document.title,
      meta: {},
      links: {},
    };

    const setMeta = (selector, attr, value) => {
      if (value == null) return;
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        // Selectors come in two flavours: name=... or property=...
        const m = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      prev.meta[selector] = {
        existed: !created,
        previous: el.getAttribute(attr),
        node: el,
      };
      el.setAttribute(attr, value);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      prev.links[rel] = {
        existed: !created,
        previous: el.getAttribute('href'),
        node: el,
      };
      el.setAttribute('href', href);
    };

    if (title) {
      const needsSuffix = appendSuffix && !/kids ?wise ?app/i.test(title);
      document.title = needsSuffix ? `${title} · Kids WiseApp` : title;
    }

    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[property="twitter:description"]', 'content', description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', 'content', document.title);
      setMeta('meta[property="twitter:title"]', 'content', document.title);
    }
    if (image) {
      setMeta('meta[property="og:image"]', 'content', image);
      setMeta('meta[property="twitter:image"]', 'content', image);
    }
    if (canonical) {
      setLink('canonical', canonical);
      setMeta('meta[property="og:url"]', 'content', canonical);
      setMeta('meta[property="twitter:url"]', 'content', canonical);
    }
    setMeta(
      'meta[name="robots"]',
      'content',
      noindex ? 'noindex, nofollow' : 'index, follow'
    );

    return () => {
      // Best-effort restore so navigating back to a default-meta page
      // doesn't keep a stale title.
      document.title = prev.title;
      Object.entries(prev.meta).forEach(([_sel, ref]) => {
        if (ref.existed) {
          if (ref.previous != null) ref.node.setAttribute('content', ref.previous);
        } else if (ref.node && ref.node.parentNode) {
          ref.node.parentNode.removeChild(ref.node);
        }
      });
      Object.entries(prev.links).forEach(([_rel, ref]) => {
        if (ref.existed) {
          if (ref.previous != null) ref.node.setAttribute('href', ref.previous);
        } else if (ref.node && ref.node.parentNode) {
          ref.node.parentNode.removeChild(ref.node);
        }
      });
    };
  }, [title, description, canonical, image, noindex, appendSuffix]);

  return null;
}

export default SEO;
